/* eslint-disable react-refresh/only-export-components */
import { useCallback, useEffect, useRef, useState, type MouseEvent, type TouchEvent } from "react";
import { CardElement, Elements, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useNavigate } from "react-router-dom";
import { routes } from "@/app/navigation/routes";
import { useAddress } from "@/shared/hooks/useAddress";
import { useAuth } from "@/app/providers/useAuth";
import { propertyService } from "@/features/properties/model/propertyServices";
import { isValidUUID } from "@/features/properties/model/guestSession";
import type { PropertyApiResponse } from "@/features/properties/model/propertiesTypes";
import {
  submitHomesteadDocuments,
  uploadHomesteadLicensePhoto,
} from "@/features/homesteadExemption/services/homestead.service";
import { handleProceedToPayment } from "@/features/protest/viewModal/stripe";
import { paymentSubscriptionService } from "@/features/protest/services";
import { Modal } from "@/shared/modal";

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ||
    "pk_test_51LB5eCDJVPEOLc88uxXxMGVWs7SJ5LpzQj9UghEY1SbYgibsNwxpShB7J2erPnLvnHqKM0eH9UY8LWlKgM9UoUB900Ddq3xT7J"
);

function getDocumentUrlFromUnknown(value: unknown): string {
  if (!value || typeof value !== "object") return "";
  const doc = value as Record<string, unknown>;
  const candidate = doc.document || doc.document_url || doc.url;
  return typeof candidate === "string" ? candidate : "";
}

type PaymentCardModalProps = {
  isProcessing: boolean;
  onClose: () => void;
  onConfirm: (paymentIntentId: string) => Promise<void>;
  clientSecret: string;
  paymentIntentId: string;
  styles: Record<string, string>;
};

const PaymentCardModal = ({
  isProcessing,
  onClose,
  onConfirm,
  clientSecret,
  paymentIntentId,
  styles,
}: PaymentCardModalProps) => {
  const stripe = useStripe();
  const elements = useElements();

  const onConfirmCardPayment = async () => {
    try {
      if (!stripe || !elements) {
        window.alert("Payment form is still loading. Please try again.");
        return;
      }
      const card = elements.getElement(CardElement);
      if (!card) {
        window.alert("Please enter card details.");
        return;
      }
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card },
      });
      if (result.error) {
        throw new Error(result.error.message || "Payment failed");
      }
      if (result.paymentIntent?.status === "succeeded") {
        await onConfirm(paymentIntentId);
      }
    } catch (error) {
      console.error(error);
      window.alert("Unable to complete payment. Please try again.");
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Enter Card Details"
      titleClassName={styles.paymentTitle}
      headerClassName={styles.paymentHeader}
      unconstrained
      className={styles.paymentCard}
      bodyClassName={styles.paymentCardBody}
      closeButtonDisabled={isProcessing}
      ariaLabel="Checkout payment"
    >
      <p className={styles.paymentSubtitle}>Card number, expiry date, and CVC</p>
      <div className={styles.cardElementWrap}>
        <CardElement />
      </div>
      <button
        type="button"
        className={styles.paymentPayBtn}
        disabled={isProcessing}
        onClick={() => void onConfirmCardPayment()}
      >
        {isProcessing ? "Processing..." : "Pay $149"}
      </button>
    </Modal>
  );
};

export const useHomesteadExemptionViewModal = (styles: Record<string, string>) => {
  const { user } = useAuth();
  const { selectedAddress: propertyId } = useAddress();
  const navigate = useNavigate();

  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [isSignModalOpen, setIsSignModalOpen] = useState(false);
  const [isUploadingFront, setIsUploadingFront] = useState(false);
  const [isUploadingBack, setIsUploadingBack] = useState(false);
  const [frontFileName, setFrontFileName] = useState("");
  const [backFileName, setBackFileName] = useState("");
  const [licenseFrontPhoto, setLicenseFrontPhoto] = useState("");
  const [licenseBackPhoto, setLicenseBackPhoto] = useState("");
  const [homesteadDocUrl, setHomesteadDocUrl] = useState("");
  const [exemptionDocUrl, setExemptionDocUrl] = useState("");
  const [isSubmittingSignature, setIsSubmittingSignature] = useState(false);
  const [propertyDetails, setPropertyDetails] = useState<PropertyApiResponse | null>(null);
  const [isPropertyLoading, setIsPropertyLoading] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signature, setSignature] = useState<string | null>(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [paymentIntentData, setPaymentIntentData] = useState<{
    client_secret: string;
    payment_intent_id: string;
  } | null>(null);

  const signatureCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const frontInputRef = useRef<HTMLInputElement | null>(null);
  const backInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isSignModalOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsSignModalOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      const devicePixelRatio = window.devicePixelRatio || 1;
      canvas.width = Math.floor(rect.width * devicePixelRatio);
      canvas.height = Math.floor(rect.height * devicePixelRatio);
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.scale(devicePixelRatio, devicePixelRatio);
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.lineWidth = 2;
        ctx.strokeStyle = "#111827";
      }
    }
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [isSignModalOpen]);

  const fetchProperty = useCallback(async () => {
    if (!propertyId?.trim() || !isValidUUID(propertyId)) return;
    const response = await propertyService.getPropertyById(propertyId);
    const homesteadDocs = Array.isArray(response?.documents?.property_homestead_documents)
      ? (response.documents.property_homestead_documents as Array<{
          document_side?: string;
          document?: string;
        }>)
      : [];
    const frontDoc = homesteadDocs.find((item) => item.document_side === "front_side");
    const backDoc = homesteadDocs.find((item) => item.document_side === "back_side");
    const homesteadExemptionDocs = Array.isArray(response?.documents?.homestead_exemption_documents)
      ? response.documents.homestead_exemption_documents
      : [];
    const homesteadPreviewFromDocuments = getDocumentUrlFromUnknown(homesteadExemptionDocs[0]);

    setLicenseFrontPhoto(frontDoc?.document || "");
    setLicenseBackPhoto(backDoc?.document || "");
    setPropertyDetails(response);
    setHomesteadDocUrl(homesteadPreviewFromDocuments || response.signedHomesteadDocumentUrl || "");
    setExemptionDocUrl(response.signedExemptionDocumentUrl || "");
  }, [propertyId]);

  useEffect(() => {
    if (!propertyId?.trim()) return;
    void (async () => {
      try {
        setIsPropertyLoading(true);
        await fetchProperty();
      } catch (error) {
        console.error(error);
      } finally {
        setIsPropertyLoading(false);
      }
    })();
  }, [fetchProperty, propertyId]);

  const propertyAddressTitle =
    propertyDetails?.address?.street?.trim() ||
    propertyDetails?.address?.line1?.trim() ||
    propertyDetails?.address?.full_address?.trim() ||
    "Selected Property";
  const propertyAddressMeta = [
    propertyDetails?.address?.city,
    propertyDetails?.address?.state,
    propertyDetails?.address?.zip,
  ]
    .filter(Boolean)
    .join(", ");

  const getCoordinates = (e: MouseEvent<HTMLCanvasElement> | TouchEvent<HTMLCanvasElement>) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if ("touches" in e && e.touches.length > 0) {
      const touch = e.touches.item(0);
      if (!touch) return null;
      return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    }
    if ("clientX" in e) {
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    }
    return null;
  };

  const startDrawing = (e: MouseEvent<HTMLCanvasElement> | TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const coords = getCoordinates(e);
    if (!coords) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    setIsDrawing(true);
  };

  const draw = (e: MouseEvent<HTMLCanvasElement> | TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const coords = getCoordinates(e);
    if (!coords) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    setSignature(canvas.toDataURL());
  };

  const stopDrawing = () => {
    if (isDrawing) {
      const canvas = signatureCanvasRef.current;
      if (canvas) setSignature(canvas.toDataURL());
    }
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignature(null);
  };

  const handleUpload = async (side: "front" | "back", file: File | null) => {
    if (!file || !propertyId?.trim()) {
      window.alert("Please select a property and choose a file.");
      return;
    }
    if (side === "front") setIsUploadingFront(true);
    if (side === "back") setIsUploadingBack(true);
    try {
      const res = await uploadHomesteadLicensePhoto({
        property_id: propertyId,
        documentType: "driving_licence",
        documentSide: `${side}_side`,
        file,
      });
      const message = (res.message || "").toLowerCase();
      if (res.success || message.includes("success")) {
        if (side === "front") {
          setFrontFileName(file.name);
          setLicenseFrontPhoto(URL.createObjectURL(file));
        }
        if (side === "back") {
          setBackFileName(file.name);
          setLicenseBackPhoto(URL.createObjectURL(file));
        }
      } else {
        window.alert(res.message || "Upload failed.");
      }
    } catch (error) {
      console.error(error);
      window.alert("Upload failed.");
    } finally {
      if (side === "front") setIsUploadingFront(false);
      if (side === "back") setIsUploadingBack(false);
    }
  };

  const handleSubmitSignature = async () => {
    if (!propertyId?.trim() || !signature) {
      window.alert("Property and signature are required.");
      return;
    }
    setIsSubmittingSignature(true);
    try {
      const res = await submitHomesteadDocuments({
        property_id: propertyId,
        signature,
      });
      const message = (res.message || "").toLowerCase();
      if (res.success || message.includes("success")) {
        setHomesteadDocUrl(res.data?.homestead_document_url || homesteadDocUrl);
        setExemptionDocUrl(res.data?.exemption_document_url || exemptionDocUrl);
        setPropertyDetails((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            homestead_signed: true,
            signedHomestead: true,
            signedHomesteadDocumentUrl:
              res.data?.homestead_document_url || prev.signedHomesteadDocumentUrl,
            signedExemptionDocumentUrl:
              res.data?.exemption_document_url || prev.signedExemptionDocumentUrl,
          };
        });
        // Best-effort refresh so checkout sees the latest server state too.
        try {
          await fetchProperty();
        } catch (error) {
          console.error(error);
        }
        window.alert("Homestead documents signed successfully.");
        setIsSignModalOpen(false);
      } else {
        window.alert(res.message || "Could not submit signature.");
      }
    } catch (error) {
      console.error(error);
      window.alert("Could not submit signature.");
    } finally {
      setIsSubmittingSignature(false);
    }
  };

  const isHomesteadSigned = Boolean(propertyDetails?.homestead_signed);

  const onStartCheckout = async () => {
    try {
      if (!propertyId?.trim() || !user?.id) {
        window.alert("Property and user are required to continue checkout.");
        return;
      }

      // Validate required uploads + signed signature before starting payment.
      const hasFront = Boolean(licenseFrontPhoto);
      const hasBack = Boolean(licenseBackPhoto);

      if (!hasFront) {
        window.alert("Please upload the FRONT of your driver’s license before checkout.");
        return;
      }

      if (!hasBack) {
        window.alert("Please upload the BACK of your driver’s license before checkout.");
        return;
      }

      if (!isHomesteadSigned) {
        setIsSignModalOpen(true);
        window.alert("Please sign the homestead documents before checkout.");
        return;
      }

      setIsPaymentProcessing(true);
      const intent = await handleProceedToPayment({
        amount: 149,
        useWallet: false,
        userId: user.id,
        propertyId,
      });
      setPaymentIntentData({
        client_secret: intent.client_secret,
        payment_intent_id: intent.payment_intent_id,
      });
      setIsPaymentModalOpen(true);
    } catch (error) {
      console.error(error);
      window.alert("Unable to start payment. Please try again.");
    } finally {
      setIsPaymentProcessing(false);
    }
  };

  const onConfirmPaymentSucceeded = async (paymentIntentId: string) => {
    try {
      setIsPaymentProcessing(true);
      await paymentSubscriptionService.updatePaymentStatus({
        payment_intent_id: paymentIntentId,
      });
      setIsPaymentModalOpen(false);
      setPaymentIntentData(null);
      setIsSuccessOpen(true);
    } catch (error) {
      console.error(error);
      window.alert("Unable to complete payment. Please try again.");
    } finally {
      setIsPaymentProcessing(false);
    }
  };

  const paymentModal =
    isPaymentModalOpen && paymentIntentData ? (
      <Elements stripe={stripePromise}>
        <PaymentCardModal
          styles={styles}
          isProcessing={isPaymentProcessing}
          onClose={() => setIsPaymentModalOpen(false)}
          onConfirm={onConfirmPaymentSucceeded}
          clientSecret={paymentIntentData.client_secret}
          paymentIntentId={paymentIntentData.payment_intent_id}
        />
      </Elements>
    ) : null;

  const onBack = useCallback(() => {
    // Close the top-most overlay first. If nothing is open, go back in browser history.
    if (isSignModalOpen) {
      setIsSignModalOpen(false);
      return;
    }
    if (isPaymentModalOpen) {
      setIsPaymentModalOpen(false);
      return;
    }
    if (isSuccessOpen) {
      setIsSuccessOpen(false);
      return;
    }
    navigate(-1);
  }, [isSignModalOpen, isPaymentModalOpen, isSuccessOpen, navigate]);

  return {
    navigate,
    propertyId,
    isSuccessOpen,
    setIsSuccessOpen,
    isSignModalOpen,
    setIsSignModalOpen,
    isPaymentModalOpen,
    isUploadingFront,
    isUploadingBack,
    frontFileName,
    backFileName,
    licenseFrontPhoto,
    licenseBackPhoto,
    homesteadDocUrl,
    exemptionDocUrl,
    isSubmittingSignature,
    propertyDetails,
    isPropertyLoading,
    signature,
    isPaymentProcessing,
    signatureCanvasRef,
    frontInputRef,
    backInputRef,
    propertyAddressTitle,
    propertyAddressMeta,
    startDrawing,
    draw,
    stopDrawing,
    clearSignature,
    handleUpload,
    handleSubmitSignature,
    onStartCheckout,
    paymentModal,
    routes,
    onBack,
  };
};
